import React, { ChangeEvent, KeyboardEvent, useState } from "react";
import { useFormikContext } from "formik";
import { twMerge } from "tailwind-merge";

import { Icon } from "@ui/icons/icon";
import { ICON_COLLECTION } from "@ui/icons/icon-list";

import { CreateFundFormType } from "@/pages/create-fund/types";
import { Token } from "@/pages/create-fund/components/select-tokens/select-tokens";

import styles from "../../create-fund-form/create-fund-form.module.scss";

const distributeEvenly = (count: number): number[] => {
  if (count === 0) return [];
  const base = Math.floor(100 / count);
  const factors = Array(count).fill(base);
  let rest = 100 - base * count;
  for (let i = 0; rest > 0; i++, rest--) {
    factors[i] += 1;
  }
  return factors;
};

interface SelectedTokensRowProps {
  token: Token;
}

const isDistributionChanged = (arr: Token[]) => {
  if (arr.length === 0) return false;

  const initDistribution = arr[0]?.distribution;
  return arr.every((token) => token.distribution === initDistribution);
};

export const SelectedTokensRow: React.FC<SelectedTokensRowProps> = ({
  token,
}) => {
  const { values, setFieldValue } = useFormikContext<CreateFundFormType>();
  const [decimalError, setDecimalError] = useState(false);
  const tokenId = token.token_id || token.policy_id;
  const priceLovelace = token.token_price_lovelace;
  const isPriceAvailable = typeof priceLovelace === "number";
  const priceClassName = "text-xs text-gray-500 dark:text-gray-400";
  const unavailableClassName = "text-red-500 dark:text-red-400";
  const priceText = isPriceAvailable
    ? (priceLovelace / 1_000_000).toLocaleString("en", {
        maximumFractionDigits: 6,
      })
    : null;

  const handleDelete = () => {
    const filteredIds = values.selectedTokenIds.filter((id) => id !== tokenId);

    let newTokens = Object.fromEntries(
      filteredIds.map((id) => [id, values.selectedTokens[id]]),
    );

    const shouldRecalculateDistribution = isDistributionChanged(
      filteredIds.map((id) => values.selectedTokens[id]),
    );

    if (shouldRecalculateDistribution) {
      const factors = distributeEvenly(filteredIds.length);
      newTokens = Object.fromEntries(
        filteredIds.map((id, i) => [
          id,
          { ...values.selectedTokens[id], distribution: factors[i] },
        ]),
      );
    }

    setFieldValue("selectedTokenIds", filteredIds);
    setFieldValue("selectedTokens", newTokens);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "." || event.key === ",") {
      event.preventDefault();
      setDecimalError(true);
      setTimeout(() => setDecimalError(false), 2000);
    }
  };

  const handleOnChange = (event: ChangeEvent<HTMLInputElement>) => {
    const raw = event.target.value;
    if (raw === "") {
      setFieldValue(`selectedTokens.${tokenId}.distribution`, "");
      return;
    }
    const value = parseInt(raw, 10);
    setFieldValue(
      `selectedTokens.${tokenId}.distribution`,
      isNaN(value) ? 0 : value,
    );
  };

  const imageLink = token.token_image?.startsWith("ipfs")
    ? token.token_image.replace("ipfs://", "https://ipfs.io/ipfs/")
    : token.token_image;

  const displayName = token.token_symbol || token.token_name || "";
  const hasImage = !!imageLink;
  const initial = displayName.charAt(0).toUpperCase();

  return (
    <div className="flex items-center justify-between w-full py-3 border-b border-gray-100 dark:border-gray-800">
      <div className="flex items-center gap-2">
        {hasImage ? (
          <img src={imageLink} alt="" className="h-12" />
        ) : (
          <div className="h-12 w-12 rounded-full bg-violet-200 dark:bg-gray-700 flex items-center justify-center text-violet-600 dark:text-violet-300 font-semibold text-lg">
            {initial || "?"}
          </div>
        )}
        <div className="flex flex-col items-start text-left">
          <h6 className="font-sf-pro text-lg md:text-xl text-gray-800 dark:text-white font-semibold text-left">
            {displayName}
          </h6>
          {isPriceAvailable ? (
            <span className={priceClassName}>Price: {priceText} ADA</span>
          ) : (
            <span className={priceClassName}>
              Price: <span className={unavailableClassName}>unavailable</span>
            </span>
          )}
        </div>
        {/*{token.verified ? (*/}
        {/*  <Icon icon={ICON_COLLECTION.circleCheck} iconColor="#7C3AED" />*/}
        {/*) : null}*/}
      </div>
      <div className="flex items-center gap-3">
        <div className="flex flex-col items-end">
          <div className={styles["input-with-addon"]}>
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              name="token-distribution"
              value={token.distribution}
              onKeyDown={handleKeyDown}
              onChange={handleOnChange}
              className="px-3 pr-8 py-2 border border-gray-200 dark:border-gray-800 dark:bg-gray-800 rounded-lg font-sf-pro text-gray-800 dark:text-white w-[80px]"
            />
            <span className={twMerge(styles["input-addon"], "dark:text-white")}>
              %
            </span>
          </div>
          {decimalError && (
            <p className="text-xs text-red-400 mt-1">Must be a whole number</p>
          )}
        </div>
        <button
          onClick={handleDelete}
          className="flex items-center justify-center"
        >
          <Icon
            icon={ICON_COLLECTION.xCircle}
            className="height-24"
            iconColor="#6B7280"
            hoverColor="#EF4444"
          />
        </button>
      </div>
    </div>
  );
};
